import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type { MeetingPageDocument } from '@wundo/shared/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingDocumentDeleteDialogProps {
  /** The document being thrown away, or null while none is. */
  document: MeetingPageDocument | null;
  failed: boolean;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The confirm in front of removing a document from a conversation.
 *
 * It says how far the removal reaches: the document hangs on the conversation,
 * so it goes from every meeting in the series at once, not only from the page
 * it is being deleted on. The file itself goes with it — Wundo keeps no copy.
 */
export function MeetingDocumentDeleteDialog({
  document,
  failed,
  pending,
  onConfirm,
  onCancel,
}: MeetingDocumentDeleteDialogProps) {
  return (
    <Dialog open={document !== null} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_document_delete_title()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-2.5 px-5 py-4'>
          <DialogDescription>
            {m.meeting_document_delete_body({ name: document?.name ?? '' })}
          </DialogDescription>
          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_document_delete_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_cancel()}
          </Button>
          <Button size='sm' variant='danger' disabled={pending} onClick={onConfirm}>
            {m.meeting_document_delete_confirm()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
